import { forwardRef } from 'react';
import { styled, Theme, lighten } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AlertTitle from '@mui/material/AlertTitle';
import MuiAlert, { AlertProps as MuiAlertProps } from '@mui/material/Alert';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CustomContentProps, closeSnackbar } from 'notistack';

// Type
interface ActionItem {
  label: string;
  action: () => void;
}

interface NotifyAlertProps extends CustomContentProps {
  title?: string;
  actions?: ActionItem[];
}
interface AlertProps extends MuiAlertProps {
  handleClose?: () => void;
  enableCloseBtn?: boolean;
  actions?: ActionItem[];
}

// Component
// Notistack 專用 Alert
export const NotifyAlert = forwardRef<HTMLDivElement, NotifyAlertProps>(
  (props, ref) => {
    const { id, message, actions, variant, style } = props;

    return (
      <Alert
        ref={ref}
        style={style}
        severity={variant === 'default' ? undefined : variant}
        handleClose={() => closeSnackbar(id)}
        actions={actions}
        enableCloseBtn
      >
        {message}
      </Alert>
    );
  }
);

export const InfoAlert = forwardRef<HTMLDivElement, NotifyAlertProps>(
  (props, ref) => {
    const { id, title, message, actions, variant, style } = props;

    return (
      <Alert
        className="info-alert"
        enableCloseBtn
        ref={ref}
        style={style}
        actions={actions}
        handleClose={() => closeSnackbar(id)}
        severity={variant === 'default' ? undefined : variant}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {message}
      </Alert>
    );
  }
);

// 通用 Alert
export const UnstyledAlert = forwardRef<HTMLDivElement, AlertProps>(
  (props, ref) => {
    const { handleClose, enableCloseBtn, severity, actions, ...rest } = props;

    const renderActions = (actions: ActionItem[]) =>
      actions.map((item) => (
        <Button size="small" onClick={item.action} key={item.label}>
          {item.label}
        </Button>
      ));

    return (
      <MuiAlert
        ref={ref}
        severity={severity}
        iconMapping={{
          error: <HighlightOffIcon fontSize="inherit" />,
          warning: <ErrorOutlineIcon fontSize="inherit" />,
          success: <CheckCircleOutlineIcon fontSize="inherit" />,
        }}
        {...(!severity && { icon: <InfoOutlinedIcon fontSize="inherit" /> })}
        action={
          <>
            {renderActions(actions || [])}
            {enableCloseBtn && (
              <IconButton
                size="small"
                onClick={() => {
                  handleClose && handleClose();
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </>
        }
        {...rest}
      />
    );
  }
);

// Style
const Alert = styled(
  forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
    <UnstyledAlert ref={ref} {...props} />
  ))
)(({ theme, severity }) => ({
  backgroundColor: getBgColor(theme, severity),

  boxShadow: theme.shadows[2],
  color: grey[800],
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(1.5),
  ...theme.typography.body2,

  '.MuiAlert-icon': {
    color: getIconColor(theme, severity),

    marginRight: theme.spacing(1),
    paddingBottom: '0.55rem',
    paddingTop: '0.55rem',
    fontSize: '1rem',
  },

  '.MuiAlert-message': {
    maxHeight: `calc(3rem + ${theme.spacing(2)})`,
    maxWidth: 350,
    whiteSpace: 'pre-line',
  },
  '&.info-alert .MuiAlert-message': {
    maxHeight: 'none',
    maxWidth: 'none',
  },

  '.MuiAlert-action': {
    paddingLeft: theme.spacing(2.5),
    paddingTop: 0,
    alignItems: 'center',

    '.MuiButton-text': {
      marginRight: theme.spacing(1.75),
      textTransform: 'none',
      fontSize: '0.875rem',
      fontWeight: 700,
    },
  },
}));

const getBgColor = (theme: Theme, severity: string | undefined) => {
  switch (severity) {
    case 'error':
      return lighten(theme.palette.error.light, 0.8);
    case 'warning':
      return lighten(theme.palette.warning.light, 0.8);
    case 'success':
      return lighten(theme.palette.success.light, 0.8);
    case 'info':
      return lighten(theme.palette.info.light, 0.8);
    default:
      return grey[100];
  }
};

const getIconColor = (theme: Theme, severity: string | undefined) => {
  switch (severity) {
    case 'error':
      return theme.palette.error.dark;
    case 'warning':
      return theme.palette.warning.dark;
    case 'success':
      return theme.palette.success.dark;
    case 'info':
      return theme.palette.info.dark;
    default:
      return grey[700];
  }
};

export default Alert;
// Reference: https://notistack.com/features/customization
